import { NoddiButton, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { AddMemberStepLayout } from './AddMemberStepLayout';
import type { AddMemberDraft } from './addMemberWizardSteps';
import { MemberContactInfo } from './MemberContactInfo';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { SectionHeading } from './SectionHeading';

const personSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  email: z.union([z.literal(''), z.email()])
});

const FORM_ID = 'add-member-person-form';

type Props = {
  /** True when no account has the number yet, so this step creates one instead of confirming it. */
  needsName: boolean;
  /** First name and last-name initial of the matched account; the admin confirms the right person. */
  displayName: string;
  phoneNumber: string;
  draft: AddMemberDraft;
  translations: NotificationGroupOverviewTranslations;
  onBack: (draft: AddMemberDraft) => void;
  onSubmit: (draft: AddMemberDraft) => void;
};

export const AddMemberPersonStep = ({
  needsName,
  displayName,
  phoneNumber,
  draft,
  translations: t,
  onBack,
  onSubmit
}: Props) => {
  const form = useAppForm({
    defaultValues: draft satisfies z.input<typeof personSchema>,
    validators: { onSubmit: personSchema },
    onSubmit: ({ value }) => onSubmit(value)
  });

  // A matched account is shown, not described: the admin recognizes the person or corrects the number.
  if (!needsName) {
    return (
      <AddMemberStepLayout
        secondary={{ label: t.back, onClick: () => onBack(draft), icon: 'ArrowLeft' }}
        action={
          <NoddiButton className='flex-[2]' onClick={() => onSubmit(draft)}>
            {t.next}
          </NoddiButton>
        }
      >
        <div className='rounded-xl border border-border/60 p-3'>
          <MemberContactInfo firstName={displayName} lastName='' phoneNumber={phoneNumber} />
        </div>
        <p className='text-sm text-muted-foreground'>{t.memberFoundDescription}</p>
      </AddMemberStepLayout>
    );
  }

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className='flex min-h-0 flex-1 flex-col gap-4'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <AddMemberStepLayout
          secondary={{ label: t.back, onClick: () => onBack(form.state.values), icon: 'ArrowLeft' }}
          action={<form.SubscribeButton formId={FORM_ID} label={t.next} className='flex-[2]' />}
        >
          <section className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <SectionHeading icon='User'>{t.contactDetails}</SectionHeading>
              <p className='text-sm text-muted-foreground'>{t.memberNotFound}</p>
            </div>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <form.AppField name='firstName'>{(field) => <field.Input label={t.firstName} />}</form.AppField>
              <form.AppField name='lastName'>{(field) => <field.Input label={t.lastName} />}</form.AppField>
            </div>
            <form.AppField name='email'>
              {(field) => <field.Input type='email' label={t.emailOptional} />}
            </form.AppField>
          </section>
        </AddMemberStepLayout>
      </form>
    </form.AppForm>
  );
};
